import React from 'react';
import { useTranslation } from 'react-i18next';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { MonitoringType } from 'shared/components/monitoring/monitoring-type';
import SearchBar from 'shared/components/search-bar/search-bar';
import FilterChip from 'shared/components/table-filter-chips/filter-chip/filter-chip';
import { FilterChipOption } from 'shared/components/table-filter-chips/multi-value-filter-chip/models/filter-chip-option';
import MultiValueFilterChip from 'shared/components/table-filter-chips/multi-value-filter-chip/multi-value-filter-chip';
import { DeviceType } from 'shared/models/device-type';
import KeyValuePair from 'shared/models/key-value-pair';

import DeviceFilter from 'features/device/models/device-filter';
import DeviceFilterValue, { FilterValueType } from 'features/device/models/device-filter-value';
import ReadFilterValuesMonitoring from '../domain/models/read-filter-values-monitoring';
import DeviceMonitoringFilter from './components/device-monitoring-filter';

import './device-filters.scss';

interface IProps {
  monitoringValues: ReadFilterValuesMonitoring[];
  filterValues: DeviceFilterValue[];
  activeFilters: DeviceFilter[];
  activeMonitoringFilters: ReadFilterValuesMonitoring[];
  searchBarValue: string;
  onOptionsSelected: (filterValueType: FilterValueType, keys: string[]) => void;
  onMonitoringFilterSelected: (selected: boolean, type: MonitoringType) => void;
  onSearchValueChanged: (value: string) => void;
  clearFilters: () => void;
  refresh?: () => void;
  disabled?: boolean;
}

const DeviceFilters = (props: Readonly<IProps>): React.JSX.Element => {
  const { t } = useTranslation('devices');
  const { t: deviceTypesTranslations } = useTranslation('deviceTypes');

  const { filterValues } = props;

  const determineSelectedOptions = (filterValue: DeviceFilterValue): KeyValuePair[] => {
    let selectedOptions: KeyValuePair[] = [];

    const currentSelectedKeys = props.activeFilters.find((filter) => filter.filterValueType === filterValue.filterValueType)?.values;

    if (currentSelectedKeys) {
      currentSelectedKeys.forEach((currentSelectedKey) => {
        const currentSelectedValue = getFilterValues(filterValue).find((value) => value.key === currentSelectedKey)?.value;

        if (!currentSelectedValue) {
          selectedOptions = [];
          props.clearFilters();
          return;
        }

        selectedOptions.push({
          key: currentSelectedKey,
          value: currentSelectedValue,
        } as KeyValuePair);
      });
    }
    return selectedOptions;
  };

  const determineSelectedMultiValueOptions = (filterValue: DeviceFilterValue): FilterChipOption[] => {
    return determineSelectedOptions(filterValue).map((x) => ({
      key: x.key,
      label: x.value,
      value: [x.key],
    }));
  };

  const getFilterValues = (filter: DeviceFilterValue): KeyValuePair[] => {
    if (filter.filterValueType === FilterValueType.Type) {
      return filter.values.map((x) => ({
        key: x.key,
        value: deviceTypesTranslations(`${DeviceType[x.key as keyof typeof DeviceType]}`),
      }));
    }

    if (filter.filterValueType === FilterValueType.Linked) {
      return filter.values.map((x) => ({
        key: x.key,
        value: x.key === 'true' ? t(`filters.linked`) : t(`filters.notLinked`),
      }));
    }

    return filter.values;
  };

  const getMultiValueFilterOptions = (filter: DeviceFilterValue): FilterChipOption[] => {
    return getFilterValues(filter).map((x) => ({
      key: x.key,
      label: x.value,
      value: [x.key],
    }));
  };

  const determinePlaceHolderText = (filterValueType: FilterValueType): string => {
    switch (filterValueType) {
      case FilterValueType.Type:
        return t('filters.type');
      case FilterValueType.Source:
        return t('filters.source');
      case FilterValueType.Linked:
        return t('filters.link');
      default:
        return '';
    }
  };

  const clearFilters = (): void => {
    props.clearFilters();
  };

  const isMultiValueFilter = (value: FilterValueType): boolean => {
    return value === FilterValueType.Type || value === FilterValueType.Source;
  };

  const getFirstSelectedOption = (filterValue: DeviceFilterValue): KeyValuePair | undefined => {
    const options = determineSelectedOptions(filterValue);
    return options.length > 0 ? options[0] : undefined;
  };

  const getCountForType = (monitoringType: MonitoringType): number => {
    return props.monitoringValues.find((x) => x.monitoringType === monitoringType)?.count ?? 0;
  };

  return (
    <div className="device-filters-container">
      <div className="top-row">
        <div className="device-filters">
          <SearchBar {...props} searchValue={props.searchBarValue} disabled={props.disabled} />
          {filterValues.length > 0 && (
            <>
              {filterValues.map((filterValue) =>
                isMultiValueFilter(filterValue.filterValueType) ? (
                  <MultiValueFilterChip
                    key={`multiValuefilterChip${filterValue.filterValueType}`}
                    options={getMultiValueFilterOptions(filterValue)}
                    selectedOptions={determineSelectedMultiValueOptions(filterValue)}
                    onOptionsSelected={(keys: string[]) => props.onOptionsSelected(filterValue.filterValueType, keys)}
                    placeHolder={determinePlaceHolderText(filterValue.filterValueType)}
                    disabled={props.disabled}
                  />
                ) : (
                  <FilterChip
                    key={`filterChip${filterValue.filterValueType}`}
                    options={getFilterValues(filterValue)}
                    onOptionSelected={(key?: string) => props.onOptionsSelected(filterValue.filterValueType, key ? [key] : [])}
                    placeHolder={determinePlaceHolderText(filterValue.filterValueType)}
                    initialSelectedOption={getFirstSelectedOption(filterValue)}
                    disabled={props.disabled}
                  />
                ),
              )}
              <div className="clear-filters">
                <Button variant="text" color="primary" onClick={clearFilters} disabled={props.disabled}>
                  {t('filters.clearFilters')}
                </Button>
              </div>
            </>
          )}
        </div>

        {props.refresh && (
          <IconButton className="refresh-button" onClick={props.refresh} disabled={props.disabled}>
            <RefreshRounded />
          </IconButton>
        )}
      </div>
      {props.monitoringValues.length > 0 && (
        <div className="monitoring-filters">
          <DeviceMonitoringFilter.Offline
            count={getCountForType(MonitoringType.Offline)}
            isSelected={props.activeMonitoringFilters.find((x) => x.monitoringType === MonitoringType.Offline) !== undefined}
            onSelected={(selected) => props.onMonitoringFilterSelected(selected, MonitoringType.Offline)}
          />
          <DeviceMonitoringFilter.Outdated
            count={getCountForType(MonitoringType.OutDated)}
            isSelected={props.activeMonitoringFilters.find((x) => x.monitoringType === MonitoringType.OutDated) !== undefined}
            onSelected={(selected) => props.onMonitoringFilterSelected(selected, MonitoringType.OutDated)}
          />
          <DeviceMonitoringFilter.DeviceMalfunction
            count={getCountForType(MonitoringType.DeviceMalfunction)}
            isSelected={props.activeMonitoringFilters.find((x) => x.monitoringType === MonitoringType.DeviceMalfunction) !== undefined}
            onSelected={(selected) => props.onMonitoringFilterSelected(selected, MonitoringType.DeviceMalfunction)}
          />
          <DeviceMonitoringFilter.BatteryCombined
            count={getCountForType(MonitoringType.BatteryCombined)}
            isSelected={props.activeMonitoringFilters.find((x) => x.monitoringType === MonitoringType.BatteryCombined) !== undefined}
            onSelected={(selected) => props.onMonitoringFilterSelected(selected, MonitoringType.BatteryCombined)}
          />
          <DeviceMonitoringFilter.Online
            count={getCountForType(MonitoringType.Online)}
            isSelected={props.activeMonitoringFilters.find((x) => x.monitoringType === MonitoringType.Online) !== undefined}
            onSelected={(selected) => props.onMonitoringFilterSelected(selected, MonitoringType.Online)}
          />
          <DeviceMonitoringFilter.NotMonitored
            count={getCountForType(MonitoringType.NotMonitored)}
            isSelected={props.activeMonitoringFilters.find((x) => x.monitoringType === MonitoringType.NotMonitored) !== undefined}
            onSelected={(selected) => props.onMonitoringFilterSelected(selected, MonitoringType.NotMonitored)}
          />
        </div>
      )}
    </div>
  );
};

export default DeviceFilters;
